import { useMemo, useState, type ReactNode } from 'react';
import {
  Mail, MailCheck, MailX, Clock, Users, Search, ShieldCheck, Download,
  RefreshCw, AlertCircle, Fingerprint, Globe, MonitorSmartphone, Hash,
  CalendarClock, FileText, Building2, Loader2, MessageCircle } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { classNames, formatDate, formatDateTime, toCsv } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import Modal from '@/components/ui/Modal';

/**
 * Suscriptores de marketing captados en el portal del huésped.
 *
 * El contenedor `marketing_leads` vive en la base `guest_portal`, que es otra
 * base de la misma cuenta de Cosmos: el CRM la lee, no la duplica. La forma de
 * los registros la fija `src/lib/cosmos.ts` del portal (interfaz MarketingLead).
 *
 * Los dos tokens del registro (confirmToken y unsubscribeToken) no se declaran
 * aquí a propósito: son credenciales de un enlace y no tienen por qué pasar por
 * la pantalla de nadie.
 */
type MarketingLeadStatus = 'pending' | 'confirmed' | 'unsubscribed';

interface MarketingLead {
  id: string;
  email: string;
  name: string;
  status: MarketingLeadStatus;
  /** Siempre true: sin consentimiento el portal rechaza el alta, no la guarda. */
  consent: boolean;
  /** Versión del texto legal aceptado, para saber a qué consintió cada uno. */
  consentTextVersion: string;
  consentedAt: string;
  confirmedAt?: string;
  unsubscribedAt?: string;
  /** Teléfono en E.164, solo si consintió WhatsApp. */
  phone?: string;
  /** Consentimiento SEPARADO para WhatsApp: es la prueba que exige Meta. */
  whatsappConsent?: boolean;
  whatsappConsentedAt?: string;
  ip: string;
  userAgent: string;
  locale: string;
  source: string;
  reservationId: number;
  listingId: number;
  confirmationCode: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * De dónde llegó el alta. `guest_portal` es la tarjeta del portal, `checkin`
 * la app de registro de viajeros y `web` el formulario de tusereno.es («avísame
 * cuando abra la temporada»). Las de la web no llevan reserva ni alojamiento.
 */
type LeadSource = 'guest_portal' | 'checkin' | 'web';
const SOURCES: LeadSource[] = ['guest_portal', 'checkin', 'web'];
const SOURCE_LABELS: Record<LeadSource, string> = {
  guest_portal: 'Portal del huésped',
  checkin: 'Check-in',
  web: 'Web (tusereno.es)',
};
function sourceOf(lead: { source?: string }): LeadSource {
  return SOURCES.includes(lead.source as LeadSource) ? (lead.source as LeadSource) : 'guest_portal';
}

const STATUS_LABELS: Record<MarketingLeadStatus, string> = {
  pending: 'Pendiente de confirmar',
  confirmed: 'Confirmado',
  unsubscribed: 'Dado de baja',
};

const STATUS_BADGES: Record<MarketingLeadStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-green-100 text-green-700',
  unsubscribed: 'bg-gray-100 text-gray-500',
};

const TILES: {
  key: 'all' | MarketingLeadStatus;
  label: string;
  hint: string;
  icon: ReactNode;
  value: string;
  border: string;
}[] = [
  {
    key: 'all',
    label: 'Total',
    hint: 'Altas registradas en el portal',
    icon: <Users size={15} />,
    value: 'text-sereno-dark',
    border: 'border-sereno-dark/15',
  },
  {
    key: 'confirmed',
    label: 'Confirmados',
    hint: 'Han validado el email: se les puede escribir',
    icon: <MailCheck size={15} />,
    value: 'text-green-600',
    border: 'border-green-400',
  },
  {
    key: 'pending',
    label: 'Pendientes',
    hint: 'Falta que pulsen el enlace de confirmación',
    icon: <Clock size={15} />,
    value: 'text-amber-600',
    border: 'border-amber-400',
  },
  {
    key: 'unsubscribed',
    label: 'Bajas',
    hint: 'Han pedido no recibir más correos',
    icon: <MailX size={15} />,
    value: 'text-gray-500',
    border: 'border-gray-300',
  },
];

/** Búsqueda tolerante a mayúsculas y tildes: "Jose" encuentra a "José". */
function normalize(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}


function StatusBadge({ status }: { status: MarketingLeadStatus }) {
  return (
    <span className={classNames('badge gap-1', STATUS_BADGES[status] || 'bg-gray-100 text-gray-500')}>
      {status === 'confirmed' && <MailCheck size={12} />}
      {status === 'pending' && <Clock size={12} />}
      {status === 'unsubscribed' && <MailX size={12} />}
      {STATUS_LABELS[status] || status}
    </span>
  );
}

/** Última fecha con significado para cada estado, para no añadir tres columnas. */
function lastEvent(lead: MarketingLead): string {
  if (lead.status === 'confirmed' && lead.confirmedAt) return `Confirmado el ${formatDate(lead.confirmedAt)}`;
  if (lead.status === 'unsubscribed' && lead.unsubscribedAt) return `Baja el ${formatDate(lead.unsubscribedAt)}`;
  if (lead.status === 'pending') return 'Sin confirmar todavía';
  return '';
}

function ProofRow({ icon, label, value, mono }: { icon: ReactNode; label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-gray-50 last:border-0">
      <span className="mt-0.5 text-sereno-dark/30 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-sereno-dark/50">{label}</div>
        <div className={classNames('text-sm text-sereno-dark break-words', mono ? 'font-mono text-[13px]' : '')}>
          {value || <span className="text-sereno-dark/30">—</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * La prueba de consentimiento vive aquí, en un detalle, y no en la tabla: es lo
 * que hay que poder enseñar si alguien reclama, pero se consulta una vez al año
 * y en la lista solo sería ruido.
 */
function ConsentProof({ lead }: { lead: MarketingLead }) {
  return (
    <div>
      <div className="flex items-start gap-2 rounded-lg bg-sereno-blue/10 border border-sereno-dark/10 px-3 py-2.5 mb-4">
        <ShieldCheck size={16} className="mt-0.5 text-sereno-dark/50 shrink-0" />
        <p className="text-xs text-sereno-dark/60 leading-relaxed">
          Estos datos son el respaldo del alta: qué texto aceptó, cuándo y desde dónde.
          Consérvalos mientras el suscriptor siga en la lista.
        </p>
      </div>

      {!lead.consent && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 mb-4">
          <AlertCircle size={16} className="mt-0.5 text-red-600 shrink-0" />
          <p className="text-xs text-red-700 leading-relaxed">
            Este registro no tiene marca de consentimiento explícito. No lo uses para enviar campañas
            y revisa cómo se dio de alta.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        <ProofRow icon={<Mail size={14} />} label="Email" value={lead.email} mono />
        <ProofRow icon={<Users size={14} />} label="Nombre facilitado" value={lead.name} />
        <ProofRow icon={<CalendarClock size={14} />} label="Consentimiento otorgado" value={lead.consentedAt ? formatDateTime(lead.consentedAt) : ''} />
        <ProofRow icon={<FileText size={14} />} label="Versión del texto aceptado" value={lead.consentTextVersion} mono />
        <ProofRow icon={<MailCheck size={14} />} label="Confirmado (doble opt-in)" value={lead.confirmedAt ? formatDateTime(lead.confirmedAt) : ''} />
        <ProofRow icon={<MailX size={14} />} label="Baja" value={lead.unsubscribedAt ? formatDateTime(lead.unsubscribedAt) : ''} />
        <ProofRow
          icon={<MessageCircle size={14} />}
          label="WhatsApp"
          value={
            lead.whatsappConsent
              ? `${lead.phone || 'sin número'}${lead.whatsappConsentedAt ? ' · ' + formatDateTime(lead.whatsappConsentedAt) : ''}`
              : 'No consentido'
          }
          mono={!!lead.whatsappConsent}
        />
        <ProofRow icon={<Fingerprint size={14} />} label="IP de origen" value={lead.ip} mono />
        <ProofRow icon={<Globe size={14} />} label="Idioma del portal" value={lead.locale} />
        <ProofRow icon={<MonitorSmartphone size={14} />} label="Navegador" value={lead.userAgent} mono />
        <ProofRow icon={<Hash size={14} />} label="Origen" value={`${SOURCE_LABELS[sourceOf(lead)]} (${lead.source})`} />
        <ProofRow
          icon={<Hash size={14} />}
          label="Reserva desde la que se suscribió"
          value={lead.confirmationCode ? `${lead.confirmationCode} (#${lead.reservationId})` : lead.reservationId || ''}
          mono
        />
        <ProofRow icon={<Building2 size={14} />} label="Alojamiento" value={lead.listingId || ''} mono />
        <ProofRow icon={<CalendarClock size={14} />} label="Alta en la lista" value={lead.createdAt ? formatDateTime(lead.createdAt) : ''} />
        <ProofRow icon={<CalendarClock size={14} />} label="Última modificación" value={lead.updatedAt ? formatDateTime(lead.updatedAt) : ''} />
      </div>
    </div>
  );
}

export default function SuscritosPage() {
  const [statusFilter, setStatusFilter] = useState<'all' | MarketingLeadStatus>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | LeadSource>('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<MarketingLead | null>(null);

  // Se pide la lista entera, sin los filtros del servidor: los contadores de
  // arriba y el CSV de confirmados necesitan el total real, no el subconjunto
  // que se está mirando. Son unos cientos de registros como mucho, así que
  // filtrar y buscar en memoria sale más barato que ir al servidor en cada
  // tecla, y deja los contadores estables mientras se navega.
  const { data, loading, error, refresh } = useApi<MarketingLead[]>(
    () => api.getGuestPortalLeads(),
    [],
  );

  const leads = useMemo(() => data ?? [], [data]);

  const counts = useMemo(() => {
    const acc: Record<MarketingLeadStatus, number> = { pending: 0, confirmed: 0, unsubscribed: 0 };
    for (const lead of leads) {
      if (acc[lead.status] !== undefined) acc[lead.status] += 1;
    }
    return { ...acc, all: leads.length };
  }, [leads]);

  const confirmed = useMemo(() => leads.filter(l => l.status === 'confirmed'), [leads]);

  const sourceCounts = useMemo(() => {
    const acc: Record<LeadSource, number> = { guest_portal: 0, checkin: 0, web: 0 };
    for (const lead of leads) acc[sourceOf(lead)] += 1;
    return acc;
  }, [leads]);

  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    return leads
      .filter(l => statusFilter === 'all' || l.status === statusFilter)
      .filter(l => sourceFilter === 'all' || sourceOf(l) === sourceFilter)
      .filter(l => !q || normalize(`${l.name || ''} ${l.email || ''}`).includes(q))
      // El endpoint ya devuelve ordenado por fecha, pero la vista no depende de eso.
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [leads, statusFilter, sourceFilter, search]);

  /**
   * Exporta SIEMPRE todos los confirmados, no lo que se ve en pantalla: el
   * fichero va a una herramienta de emailing y un filtro olvidado se traduciría
   * en media lista sin recibir la campaña.
   */
  function handleExport() {
    const headers = [
      'email', 'nombre', 'confirmado_el', 'consentimiento_el', 'version_texto',
      'whatsapp', 'telefono', 'whatsapp_consentido_el',
      'ip', 'idioma', 'origen', 'reserva', 'alojamiento',
    ];
    const rows = confirmed.map(l => [
      l.email, l.name, l.confirmedAt || '', l.consentedAt || '', l.consentTextVersion,
      l.whatsappConsent ? 'sí' : 'no', l.phone || '', l.whatsappConsentedAt || '',
      l.ip, l.locale, l.source, l.confirmationCode, l.listingId,
    ]);
    const blob = new Blob([toCsv([headers, ...rows])], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `suscriptores-confirmados-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  const isFiltering = statusFilter !== 'all' || sourceFilter !== 'all' || search.trim() !== '';
  const clearFilters = () => { setStatusFilter('all'); setSourceFilter('all'); setSearch(''); };

  return (
    <div>
      {/* Cabecera */}
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2">
          <Mail size={24} className="text-sereno-dark" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Suscritos</h1>
            <p className="text-sm text-sereno-dark/40 mt-0.5">
              Suscriptores captados en el portal del huésped, el check-in y tusereno.es, con doble opt-in.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="btn-ghost flex items-center gap-2 text-sm"
            title="Recargar la lista"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            <span className="hidden sm:inline">Actualizar</span>
          </button>
          <button
            onClick={handleExport}
            disabled={confirmed.length === 0}
            className="btn-primary flex items-center gap-2 text-sm disabled:opacity-40"
            title={
              confirmed.length === 0
                ? 'No hay suscriptores confirmados que exportar'
                : `Descargar ${confirmed.length} suscriptor${confirmed.length === 1 ? '' : 'es'} confirmado${confirmed.length === 1 ? '' : 's'} en CSV`
            }
          >
            <Download size={16} />
            <span className="hidden sm:inline">Exportar confirmados</span>
            <span className="sm:hidden">CSV</span>
            {confirmed.length > 0 && <span className="opacity-70">({confirmed.length})</span>}
          </button>
        </div>
      </div>

      {/* Contadores por estado, que son a la vez el filtro */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        {TILES.map(tile => {
          const active = statusFilter === tile.key;
          return (
            <button
              key={tile.key}
              onClick={() => setStatusFilter(tile.key)}
              aria-pressed={active}
              className={classNames(
                'text-left bg-white rounded-xl border-2 p-4 transition-colors',
                active ? tile.border : 'border-gray-100 hover:border-slate-300',
              )}
            >
              <div className="flex items-center gap-1.5 text-xs font-medium text-sereno-dark/50">
                <span className={tile.value}>{tile.icon}</span>
                {tile.label}
              </div>
              <div className={classNames('text-2xl font-bold mt-1', tile.value)}>
                {counts[tile.key]}
              </div>
              <div className="text-[11px] text-sereno-dark/40 mt-0.5 leading-snug">{tile.hint}</div>
            </button>
          );
        })}
      </div>

      {/* Origen, como segundo filtro. Sirve para separar a quien se apuntó desde
          la web esperando la temporada siguiente de quien ya ha sido huésped. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs text-sereno-dark/50 mr-1">Origen:</span>
        {(['all', ...SOURCES] as const).map(key => {
          const active = sourceFilter === key;
          const label = key === 'all' ? 'Todos' : SOURCE_LABELS[key];
          const n = key === 'all' ? leads.length : sourceCounts[key];
          return (
            <button
              key={key}
              onClick={() => setSourceFilter(key)}
              aria-pressed={active}
              className={classNames(
                'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                active
                  ? 'bg-sereno-dark text-white border-sereno-dark'
                  : 'bg-white text-sereno-dark/60 border-gray-200 hover:border-slate-300',
              )}
            >
              {label} <span className={active ? 'opacity-70' : 'text-sereno-dark/40'}>({n})</span>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-sereno-dark/40 mb-6 leading-relaxed">
        Solo los <strong className="font-semibold text-sereno-dark/60">confirmados</strong> pueden recibir campañas:
        son los que han validado su email pulsando el enlace del doble opt-in. Los pendientes y las bajas se
        conservan como prueba del alta, no como destinatarios.
      </p>

      {/* Buscador */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/40" />
            <input
              className="input pl-9"
              placeholder="Buscar por email o nombre…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {isFiltering && (
            <div className="flex items-center gap-3 text-sm text-sereno-dark/50">
              <span>{filtered.length} de {leads.length}</span>
              <button onClick={clearFilters} className="btn-ghost text-xs py-1 px-2">
                Quitar filtros
              </button>
            </div>
          )}
        </div>
      </div>

      {error ? (
        <div className="card">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="mt-0.5 text-red-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-sereno-dark">No se han podido cargar los suscriptores</p>
              <p className="text-xs text-sereno-dark/50 mt-1">{error}</p>
              <button onClick={refresh} className="btn-secondary text-sm mt-3 flex items-center gap-2">
                <RefreshCw size={14} /> Reintentar
              </button>
            </div>
          </div>
        </div>
      ) : loading ? (
        <LoadingSpinner />
      ) : leads.length === 0 ? (
        /* Hoy este es el estado normal: el portal acaba de empezar a captar. */
        <div className="card text-center py-12">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sereno-blue/50 text-sereno-dark/50 mb-4">
            <Mail size={22} />
          </div>
          <h3 className="text-lg font-medium text-sereno-dark/70">Todavía no hay suscriptores</h3>
          <p className="text-sm text-sereno-dark/50 mt-2 max-w-md mx-auto leading-relaxed">
            La lista está vacía porque aún nadie se ha suscrito desde el portal, el check-in o la web, no
            porque algo haya fallado. Cada alta pasa por doble opt-in: aparecerá aquí como pendiente en cuanto
            alguien deje su email, y pasará a confirmada cuando pulse el enlace del correo.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Ningún suscriptor coincide"
          description="Prueba con otro texto o cambia el estado seleccionado."
          action={
            <button onClick={clearFilters} className="btn-primary text-sm">
              Quitar filtros
            </button>
          }
        />
      ) : (
        <>
          {/* Tabla (escritorio) */}
          <div className="card overflow-x-auto hidden md:block p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-sereno-dark/50 border-b border-gray-100">
                  <th className="px-4 py-3 font-medium">Suscriptor</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Alta</th>
                  <th className="px-4 py-3 font-medium text-right">Consentimiento</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(lead => (
                  <tr
                    key={lead.id}
                    onClick={() => setDetail(lead)}
                    className="border-b border-gray-50 last:border-0 hover:bg-sereno-blue/10 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-sereno-dark">{lead.email}</div>
                      <div className="text-xs text-sereno-dark/40">
                        {lead.name || <span className="italic">Sin nombre</span>}
                        <span className="text-sereno-dark/30"> · {SOURCE_LABELS[sourceOf(lead)]}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={lead.status} />
                    </td>
                    <td className="px-4 py-3 text-sereno-dark/60">
                      {formatDate(lead.createdAt)}
                      <div className="text-xs text-sereno-dark/40">{lastEvent(lead)}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setDetail(lead); }}
                        className="inline-flex items-center gap-1.5 text-xs text-sereno-dark/60 hover:text-sereno-dark px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Ver la prueba de consentimiento"
                      >
                        <ShieldCheck size={14} /> Ver prueba
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tarjetas (móvil) */}
          <div className="space-y-3 md:hidden">
            {filtered.map(lead => (
              <div key={lead.id} className="card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sereno-dark break-all">{lead.email}</div>
                    <div className="text-xs text-sereno-dark/40">
                      {lead.name || <span className="italic">Sin nombre</span>}
                      <span className="text-sereno-dark/30"> · {SOURCE_LABELS[sourceOf(lead)]}</span>
                    </div>
                  </div>
                  <StatusBadge status={lead.status} />
                </div>
                <div className="mt-2 text-xs text-sereno-dark/60">
                  Alta el {formatDate(lead.createdAt)}
                  {lastEvent(lead) && <span className="text-sereno-dark/40"> · {lastEvent(lead)}</span>}
                </div>
                <div className="mt-3 border-t border-gray-100 pt-2">
                  <button
                    onClick={() => setDetail(lead)}
                    className="inline-flex items-center gap-1.5 text-xs text-sereno-dark/60 px-2 py-1 rounded-lg hover:bg-gray-100"
                  >
                    <ShieldCheck size={14} /> Ver prueba de consentimiento
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal
        isOpen={detail !== null}
        onClose={() => setDetail(null)}
        title="Prueba de consentimiento"
        size="lg"
      >
        {detail && <ConsentProof lead={detail} />}
      </Modal>
    </div>
  );
}
